import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbAccordion, NgbPanelChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { RegExp } from 'src/app/core/config/regex';
import { Environment, EnvironmentCred } from 'src/app/core/models/project-pilot.model';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';
import { SeparationService } from 'src/app/shared/services/separation.service';

@Component({
  selector: 'app-manage-environment',
  templateUrl: './manage-environment.component.html',
  styleUrls: ['./manage-environment.component.scss']
})
export class ManageEnvironmentComponent implements OnInit {
  @Input() projectId!: number;
  @ViewChild('acc') accordion: NgbAccordion;

  public environment: Environment;
  public context: Context = {} as Context;
  public environmentForm: FormGroup;
  public searchKeyword: string = "";
  public submitted: boolean = false;
  private modified: boolean = false;
  public envTypes: string[] = [];
  public envmnts: Environment[] = [];
  public environmentCred: EnvironmentCred;
  public environmentCredForm: FormGroup;
  public types: string;
  public groupedEnvironments: { [key: string]: FormArray } = {};
  public activeEnvironmentId: number;
  public showPassword: boolean = false;
  private credentialsMap: { [envId: string]: any[] } = {};
  constructor(
    private fb: FormBuilder,
    private commonService: CommonService,
    private projectPilotService: ProjectPilotService,
    private separationService: SeparationService,
  ) { }

  ngOnInit(): void {
    this.setEnvironment([]);
    this.setEnvironmentCredential([], this.environmentForm.value.id);
    this.getEnvironments();
    this.projectPilotService.getCredentialType().subscribe(types => (this.types = types));
  }

  public getEnvironments() {
    this.projectPilotService.getEnvironmentStatus().subscribe(envTypes => {
      try {
        this.envTypes = typeof envTypes === 'string' ? JSON.parse(envTypes) : envTypes;
        this.projectPilotService.getEnvironments(this.projectId).subscribe(environments => {
          this.environments.clear();
          const envMap = new Map();
          environments.forEach(env => envMap.set(env.name, env));
          this.envTypes.forEach(type => {
            const existingData = envMap.get(type);
            this.environments.push(this.createEnvironmentFormGroup(existingData, type));
          });
        });
      } catch (error) {
        console.error("Error processing environments:", error);
        this.envTypes = [];
      }
    });
  }

  private createEnvironmentFormGroup(existingData: any, type: string): FormGroup {
    return this.fb.group({
      id: this.fb.control(existingData?.id ?? null),
      name: this.fb.control(type, [Validators.required]),
      url: this.fb.control(existingData?.url ?? '', [Validators.required]),
      description: this.fb.control(existingData?.description ?? ''),
      disabled: this.fb.control(existingData?.disabled ?? false),
      projectId: this.fb.control(this.projectId, [Validators.required]),
      projectName: this.fb.control(existingData?.projectName ?? '')
    });
  }

  get environments(): FormArray {
    return this.environmentForm.get("environments") as FormArray;
  }

  private setEnvironment(environments: Environment[]) {
    this.environmentForm = this.fb.group({
      environments: this.fb.array([])
    });
  }

  public saveEnvironment(environment: FormGroup, index: number) {
    this.submitted = true;
    environment.markAllAsTouched();
    if (environment.valid) {
      const env = environment.value as Environment;
      if (env.id) {
        this.projectPilotService.updateEnvironment(env.id, env).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const envId = parseInt(response.payload);
              this.commonService.setToastr(200, `Environment has been updated successfully!`);
              this.getEnvironmentCredentails(envId);
              this.getEnvironments();
            } else {
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      } else {
        this.projectPilotService.createEnvironment(env).subscribe({
          next: (response: any) => {
            if (!response.failed) {
              const envId = parseInt(response.payload);
              this.commonService.setToastr(200, `Environment has been saved successfully!`);
              this.getEnvironmentCredentails(envId);
              this.getEnvironments();
            } else {
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    }
  }

  public deleteEnvironment(environment: FormGroup, index: number) {
    const envData = environment.value;
    if (!envData || Object.keys(envData).length === 0 || !envData.name) {
      this.commonService.setToastr(400, 'Cannot delete: Incomplete environment data.');
      return;
    }
    const envId = environment.value.id;
    if (!envId) {
      this.environments.removeAt(index);
      return;
    }
    this.projectPilotService.deleteEnvironment(envId).subscribe({
      next: (message: Message) => {
        if (!message.failed) {
          this.commonService.setToastr(200, message.text);
          this.environments.removeAt(index);
          this.getEnvironments();
        } else {
          this.commonService.setToastr(500, message.text);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.commonService.setToastr(500, error?.error?.name || error.error);
      },
    });
  }

  public onPanelChange(event: NgbPanelChangeEvent) {
    if (event.panelId === 'some-panel-id-to-prevent') {
      event.preventDefault();
    }
    const panelId = event.panelId;
    const envIndex = this.environments.controls.findIndex(
      (e, i) => `panel-${i}` === panelId
    );

    if (envIndex >= 0) {
      const env = this.environments.at(envIndex);
      const envId = env.value.id;
      this.activeEnvironmentId = envId;

      if (envId && env.value.name) {
        this.projectPilotService.getEnvironmentCredentails(envId).subscribe((creds) => {
          const matchedCreds = creds.filter(
            (cred) => cred.environmentName === env.value.name
          );
          this.credentialsMap[envId] = matchedCreds.length > 0 ? matchedCreds : [];
          this.setEnvironmentCredential(this.credentialsMap[envId], envId);
        });
      }
    }
    this.accordion.collapseAll();
    this.accordion.expand(panelId);
  }

  // ENVIRONMENT CREDENTIAL
  private getEnvironmentCredentails(environmentId: number) {
    this.projectPilotService.getEnvironmentCredentails(environmentId).subscribe(
      (environmentCreds: EnvironmentCred[]) => {
        const filteredCreds = environmentCreds.filter(cred => cred.environmentId === environmentId);
        this.setEnvironmentCredential(filteredCreds, environmentId);
      },
    );
  }

  get environmentCreds(): FormArray {
    return this.environmentCredForm?.get("environmentCreds") as FormArray;
  }

  private setEnvironmentCredential(environmentCreds: EnvironmentCred[], environmentId: number) {
    this.environmentCredForm = this.fb.group({
      environmentCreds: this.fb.array([])
    });

    if (environmentCreds && environmentCreds.length > 0) {
      environmentCreds.forEach((env) => this.addEnvironmentCred(env));
    } else {
      this.addEnvironmentCred({ environmentId: environmentId } as EnvironmentCred);
    }
  }

  private addEnvironmentCred(environmentCred?: EnvironmentCred) {
    this.environmentCreds.push(this.fb.group({
      id: this.fb.control(environmentCred?.id || null, []),
      username: this.fb.control(environmentCred?.username, [Validators.pattern(RegExp.email)]),
      password: this.fb.control(environmentCred?.password, [Validators.pattern(RegExp.password)]),
      apiKey: this.fb.control(environmentCred?.apiKey || null, []),
      accessToken: this.fb.control(environmentCred?.accessToken || null, []),
      type: this.fb.control(environmentCred?.type || null, [Validators.required]),
      environmentId: this.fb.control(environmentCred?.environmentId || this.activeEnvironmentId, [Validators.required]),
      environmentName: this.fb.control(environmentCred?.environmentName || null, []),
      disabled: this.fb.control(environmentCred?.disabled || false, [])
    }));
  }

  public saveEnvironmentCred(environmentCred: FormGroup, index?: number) {
    this.submitted = true;
    environmentCred.markAllAsTouched();
    if (environmentCred.valid) {
      const env = environmentCred.value as EnvironmentCred;
      if (env.id) {
        this.addEnvironmentCred({ environmentId: env.environmentId } as EnvironmentCred);
        return;
      }

      if (!env.environmentId && this.activeEnvironmentId) {
        env.environmentId = this.activeEnvironmentId;
        environmentCred.patchValue({ environmentId: this.activeEnvironmentId });
      }
      this.projectPilotService.createEnvironmentCredentail(env).subscribe({
        next: (response: Message) => {
          if (!response.failed) {
            this.getEnvironmentCredentails(env.environmentId);
            this.commonService.setToastr(200, `Environment credential has been saved successfully!`);
            this.addEnvironmentCred({ environmentId: env.environmentId } as EnvironmentCred);
            environmentCred.get('password')?.reset();
          } else {
            this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
          }
        },
        error: (error: HttpErrorResponse) => {
          this.commonService.setToastr(500, error?.error?.name || error.error);
        },
      });
    }
  }

  public upsertEnvironmentCred(environmentCred: FormGroup) {
    if (environmentCred.valid) {
      const env = environmentCred.value as EnvironmentCred;
      if (env.id) {
        this.projectPilotService.updateEnvironmentCredentail(env.id, env).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.getEnvironmentCredentails(env.environmentId);
              environmentCred.get('password')?.reset();
              this.commonService.setToastr(200, `Environment credential has been updated successfully!`);
            } else {
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
        });
      }
    }
  }

  public deleteEnvironmentCred(environmentCred: FormGroup, index: number) {
    const credData = environmentCred.value;
    // if (!credData || Object.keys(credData).length === 0 || !credData.username) {
    //   this.commonService.setToastr(400, 'Cannot delete: Incomplete credential data.');
    //   return;
    // }
    const credId = environmentCred.value.id;
    const environmentId = environmentCred.value.environmentId;
    if (!credId) {
      this.environmentCreds.removeAt(index);
      return;
    }
    this.projectPilotService.deleteEnvironmentCredentail(credId).subscribe({
      next: (message: Message) => {
        if (!message.failed) {
          this.commonService.setToastr(200, message.text);
          this.environmentCreds.removeAt(index);
          this.getEnvironmentCredentails(environmentId);
        } else {
          this.commonService.setToastr(500, message.text);
        }
      },
    });
  }

}

